import React, { useCallback, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  StatusBar,
  StyleSheet,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useAppSelector } from '@/store';
import { useGetTagsQuery } from '@/store/api/corkBoard/corkBoardApi';
import { useThreadForm } from '@/hooks/residents/corkBoard/useThreadForm';
import { CreateThreadForm } from '@/components/residents/corkBoard/CreateThreadForm';
import { ConfirmationModal } from '@/components/residents/marketplace/ConfirmationModal';
import { SEMANTIC_COLORS, PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

type NavigationProp = StackNavigationProp<RootStackParamList, 'CreateThread'>;

export const CreateThreadScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const insets = useSafeAreaInsets();
  const { layoutMode } = useResponsive();
  const user = useAppSelector((state) => state.auth.user);
  const propertyId = user?.propertyId;

  const isDesktop = layoutMode === 'desktop';

  const [isDiscardVisible, setIsDiscardVisible] = useState(false);

  // Fetch tags for the property
  const { data: tags = [], isLoading: isLoadingTags } = useGetTagsQuery(
    { propertyId: propertyId ?? 0 },
    { skip: !propertyId },
  );

  const handleSuccess = useCallback((threadId: string) => {
    navigation.replace('ThreadDetail', { threadId });
  }, [navigation]);

  const form = useThreadForm({
    mode: 'create',
    onSuccess: handleSuccess,
  });

  const handleClose = useCallback(() => {
    if (form.isDirty) {
      setIsDiscardVisible(true);
    } else {
      navigation.goBack();
    }
  }, [form.isDirty, navigation]);

  const handleDiscard = useCallback(() => {
    setIsDiscardVisible(false);
    navigation.goBack();
  }, [navigation]);

  return (
    <View
      style={[
        styles.screenContainer,
        isDesktop && styles.desktopOverlay,
      ]}
    >
      {isDesktop && (
        <TouchableOpacity
          style={StyleSheet.absoluteFill}
          activeOpacity={1}
          onPress={handleClose}
        />
      )}

      <View
        style={[
          styles.card,
          isDesktop && styles.desktopCard,
          !isDesktop && Platform.OS !== 'ios' && { paddingTop: insets.top },
        ]}
      >
        {!isDesktop && <StatusBar barStyle="light-content" />}

        <KeyboardAvoidingView
          style={styles.flex}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={0}
        >
          {/* Header */}
          <View style={styles.header}>
            <TouchableOpacity
              style={styles.headerSide}
              onPress={handleClose}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <RESIDENTS_SVG_COMPONENTS.CANCEL_X width={18} height={18} color={SEMANTIC_COLORS.text.primary} />
            </TouchableOpacity>

            <Text style={styles.headerTitle}>New Post</Text>

            <TouchableOpacity
              style={[styles.headerSide, styles.postButton, !form.isValid && styles.postButtonDisabled]}
              onPress={form.handleSubmit}
              disabled={!form.isValid || form.isSubmitting}
              activeOpacity={0.7}
            >
              {form.isSubmitting ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.postButtonText}>
                  Post
                </Text>
              )}
            </TouchableOpacity>
          </View>

          {/* Form */}
          {isLoadingTags ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={SEMANTIC_COLORS.interactive.primary} />
            </View>
          ) : (
            <CreateThreadForm
              mode="create"
              tags={tags}
              title={form.title}
              onChangeTitle={form.setTitle}
              body={form.body}
              onChangeBody={form.setBody}
              selectedTagIds={form.selectedTagIds}
              onToggleTag={form.toggleTag}
              attachments={form.attachments}
              existingAttachments={form.existingAttachments}
              onAddFiles={form.addFiles}
              onRemoveFile={form.removeFile}
              onRemoveExistingAttachment={form.removeExistingAttachment}
              errors={form.errors}
              hasAttemptedSubmit={form.hasAttemptedSubmit}
              isSubmitting={form.isSubmitting}
              uploadProgress={form.uploadProgress}
              submitError={form.submitError}
            />
          )}
        </KeyboardAvoidingView>
      </View>

      <ConfirmationModal
        visible={isDiscardVisible}
        message="You have unsaved changes. Are you sure you want to discard this post?"
        onCancel={() => setIsDiscardVisible(false)}
        onProceed={handleDiscard}
        cancelText="Keep Editing"
        proceedText="Discard"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  screenContainer: {
    flex: 1,
    backgroundColor: SEMANTIC_COLORS.background.primary,
  },
  desktopOverlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    flex: 1,
    backgroundColor: SEMANTIC_COLORS.background.primary,
  },
  desktopCard: {
    flex: 0,
    position: 'relative',
    zIndex: 1,
    width: '100%',
    maxWidth: 640,
    minHeight: 480,
    maxHeight: 720,
    borderRadius: 16,
    overflow: 'hidden',
  },
  flex: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: PRIMITIVE_COLORS.gray800,
  },
  headerSide: {
    minWidth: 60,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
    textAlign: 'center',
    flex: 1,
  },
  postButton: {
    backgroundColor: SEMANTIC_COLORS.interactive.primary,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 34,
  },
  postButtonDisabled: {
    opacity: 0.4,
  },
  postButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
